/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale off -->*/
/*<!-- vale on -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Warehouse Sync Logs", description = "Write a row-level record of every sync back into your warehouse so you can analyze sync history and errors with SQL.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Warehouse Sync Logs",
        description: "Write a row-level record of every sync back into your warehouse so you can analyze sync history and errors with SQL.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "When to use Warehouse Sync Logs",
          "depth": 0,
          "id": "when-to-use-warehouse-sync-logs"
        }, {
          "value": "Set up Warehouse Sync Logs",
          "depth": 0,
          "id": "set-up-warehouse-sync-logs"
        }, {
          "value": "Required permissions",
          "depth": 1,
          "id": "required-permissions"
        }, {
          "value": "Enable logs for a source",
          "depth": 1,
          "id": "enable-logs-for-a-source"
        }, {
          "value": "Enable logs for a single sync",
          "depth": 1,
          "id": "enable-logs-for-a-single-sync"
        }, {
          "value": "Confirm it worked",
          "depth": 0,
          "id": "confirm-it-worked"
        }, {
          "value": "Schema",
          "depth": 0,
          "id": "schema"
        }, {
          "value": "Example queries",
          "depth": 0,
          "id": "example-queries"
        }, {
          "value": "Get the most common sync error",
          "depth": 1,
          "id": "get-the-most-common-sync-error"
        }, {
          "value": "Track when users entered and exited a model",
          "depth": 1,
          "id": "track-when-users-entered-and-exited-a-model"
        }, {
          "value": "Get the current rows in all models",
          "depth": 1,
          "id": "get-the-current-rows-in-all-models"
        }, {
          "value": "Detailed schema",
          "depth": 0,
          "id": "detailed-schema"
        }, {
          "value": "Changelog table",
          "depth": 1,
          "id": "changelog-table"
        }, {
          "value": "Snapshot table",
          "depth": 1,
          "id": "snapshot-table"
        }, {
          "value": "Sync runs table",
          "depth": 1,
          "id": "sync-runs-table"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "What's the performance impact of enabling Warehouse Sync Logs?",
          "depth": 1,
          "id": "whats-the-performance-impact-of-enabling-warehouse-sync-logs"
        }, {
          "value": "What are the limitations in Redshift?",
          "depth": 1,
          "id": "what-are-the-limitations-in-redshift"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      ol: "ol",
      img: "img",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Warehouse Sync Logs",
        plural: true
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Warehouse Sync Logs write a record of every row a sync processes back into your data warehouse, so you can analyze sync history with SQL instead of inspecting runs one at a time."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams analyzing sync history and errors at scale"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.a, {
                href: "/syncs/lightning-sync-engine",
                children: "Lightning sync engine"
              }), " enabled on a supported source."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "When you enable Warehouse Sync Logs, Hightouch writes a row into your warehouse for every row a sync processes, including its status and any error from processing it. You then query those logs with SQL or the BI tools already on top of your warehouse."
      }), "\n", _jsx(_components.h2, {
        id: "when-to-use-warehouse-sync-logs",
        children: "When to use Warehouse Sync Logs"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), " is the right tool for inspecting one run's rows, and it keeps row-level data for seven days. Reach for Warehouse Sync Logs when you need durable history you can query across many runs — for example, to:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Categorize"
          }), " errors across syncs with ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/sql-reference/functions-regexp.html",
            children: "regular expressions"
          }), " and surface unexpected ones."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Filter"
          }), " previously failed rows out of a ", _jsx(_components.a, {
            href: "/getting-started/concepts#models",
            children: "model"
          }), " with a ", _jsx(_components.code, {
            children: "JOIN"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Aggregate"
          }), " history to find rows that change the most — flapping rows can signal data-integrity issues."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track"
          }), " how a model's or audience's membership changed over time, such as how an ad campaign's targeted users shifted over its duration."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "#example-queries",
          children: "Example queries"
        }), " for concrete SQL."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-warehouse-sync-logs",
        children: "Set up Warehouse Sync Logs"
      }), "\n", _jsxs(_components.p, {
        children: ["Warehouse Sync Logs build on the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning sync engine"
        }), ", so enable that on your source first. Logs are off for every sync until you turn them on, either for a whole source or for a single sync."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports Warehouse Sync Logs on these sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/greenplum",
            children: "Greenplum Database"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["The user that connects your source to Hightouch must be able to write to the ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schema. Setting up the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine#warehouse-schemas",
          children: "Lightning sync engine"
        }), " grants this, so you shouldn't need any additional permissions."]
      }), "\n", _jsx(_components.h3, {
        id: "enable-logs-for-a-source",
        children: "Enable logs for a source"
      }), "\n", _jsx(_components.p, {
        children: "Enable logs on a source to capture every sync that uses it. This keeps logs available whenever you need to debug, which saves time when a sync fails unexpectedly."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Integrations > Sources"
          }), " and select your source."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click the ", _jsx(_components.strong, {
            children: "Sync logs"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the tables to write: ", _jsx(_components.strong, {
            children: "Sync snapshots"
          }), ", ", _jsx(_components.strong, {
            children: "Changelog"
          }), ", ", _jsx(_components.strong, {
            children: "Sync runs"
          }), ", and optionally ", _jsx(_components.strong, {
            children: "Audience snapshots"
          }), " and ", _jsx(_components.strong, {
            children: "Audience holdout group logs"
          }), ". The first three each have an ", _jsx(_components.strong, {
            children: "Audience syncs only"
          }), " sub-option that limits logging to audience syncs."]
        }), "\n", _jsx(_components.li, {
          children: "Save."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/warehouse-sync-logs-source.png",
          alt: "Enabling sync log tables at the source level"
        })
      }), "\n", _jsx(_components.h3, {
        id: "enable-logs-for-a-single-sync",
        children: "Enable logs for a single sync"
      }), "\n", _jsx(_components.p, {
        children: "Enable logs on one sync when you only need history for a specific workflow and want to limit storage."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the sync and click the ", _jsx(_components.strong, {
            children: "Sync logs"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the tables to write: ", _jsx(_components.strong, {
            children: "Snapshot"
          }), ", ", _jsx(_components.strong, {
            children: "Changelog"
          }), ", and ", _jsx(_components.strong, {
            children: "Sync runs"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-logs-tab.png",
          alt: "The Sync logs tab on a sync, with the Snapshot, Changelog, and Sync runs tables"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Sync logs can generate a lot of data depending on how many tables you enable\nand how often syncs run. Enable logs per source when you want history always\navailable for debugging; enable them per sync when you only need a few\nworkflows and want to keep storage down."
        })
      }), "\n", _jsx(_components.h2, {
        id: "confirm-it-worked",
        children: "Confirm it worked"
      }), "\n", _jsx(_components.p, {
        children: "After the sync's next run, query the sync runs table to confirm Hightouch is writing logs:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), " sync_id, model_name, status, started_at\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.sync_runs\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " started_at ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "desc"
          }), "\nlimit ", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), ";\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Recent runs in the result mean logging is working. If the table is empty, confirm the run finished after you enabled logging and that the Lightning sync engine is enabled on the source."
      }), "\n", _jsx(_components.h2, {
        id: "schema",
        children: "Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes sync logs into three tables in the ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schema:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Changelog"
          }), " — one row for every operation Hightouch performs, with the result and any error message."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Snapshot"
          }), " — each row's latest status in your model. Similar to the changelog, but easier to query when you only need current state."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync runs"
          }), " — a log of every sync run. ", _jsx(_components.code, {
            children: "JOIN"
          }), " it to the changelog and snapshot tables for details on when a run occurred and how it was configured."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["All syncs write to these same three tables; use the ", _jsx(_components.code, {
          children: "sync_id"
        }), " column to tell which rows belong to which sync. See ", _jsx(_components.a, {
          href: "#detailed-schema",
          children: "Detailed schema"
        }), " for every column."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For how Hightouch handles PII and retention in these tables, see ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine#table-removal",
            children: "table\nremoval"
          }), " on the Lightning sync\nengine page."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "example-queries",
        children: "Example queries"
      }), "\n", _jsxs(_components.p, {
        children: ["These examples are written for Snowflake; adapt them for other sources. Hightouch's ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/dbt-hightouch",
          children: "dbt package"
        }), " has more."]
      }), "\n", _jsx(_components.h3, {
        id: "get-the-most-common-sync-error",
        children: "Get the most common sync error"
      }), "\n", _jsxs(_components.p, {
        children: ["Group and count rows by ", _jsx(_components.code, {
          children: "failure_reason"
        }), " to find the most common error:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), "\n  failure_reason,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "count"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " c\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.sync_snapshot\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "where"
          }), " failure_reason ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "not"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "null"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " failure_reason\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " c ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "desc"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/history-example-common-errors.png",
          alt: "Result of the most common errors query"
        })
      }), "\n", _jsx(_components.h3, {
        id: "track-when-users-entered-and-exited-a-model",
        children: "Track when users entered and exited a model"
      }), "\n", _jsxs(_components.p, {
        children: ["Track when users enter and exit a model — useful with ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio audiences"
        }), " and a BI tool:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " details ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), "\n    model_name,\n    row_id,\n    op_type ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " type,\n    started_at ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "lag"
          }), "(op_type) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "over"
          }), "(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "partition"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " model_name, row_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " started_at) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " lag_type\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.sync_changelog c\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), " hightouch_audit.sync_runs r ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " c.sync_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " r.sync_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " c.sync_run_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " r.sync_run_id\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "where"
          }), " op_type ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'changed'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " model_name, row_id\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), "\n  row_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " user_id,\n  model_name ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " audience,\n  type,\n  ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " details\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "where"
          }), " (lag_type ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " type ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "or"
          }), " lag_type ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "null"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " model_name, row_id, ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/history-example-audience-changes.png",
          alt: "Result of the audience changes query"
        })
      }), "\n", _jsx(_components.h3, {
        id: "get-the-current-rows-in-all-models",
        children: "Get the current rows in all models"
      }), "\n", _jsxs(_components.p, {
        children: ["Find the most recently synced rows that didn't fail, across all models — useful for listing current members of ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/customer-studio",
          children: "Customer Studio"
        }), " audiences:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " model_names ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "distinct"
          }), "\n    sync_id,\n    model_name\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.sync_runs\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), "\n  model_name,\n  row_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " user_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.sync_snapshot s\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), " model_names r ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " s.sync_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " r.sync_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "where"
          }), " s.status ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'failed'"
          }), "\nqualify ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "row_number"
          }), "() ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "over"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "partition"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " user_id, model_name ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "null"
          }), ") ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " user_id\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/history-example-current-audiences.png",
          alt: "Result of the current audiences query"
        })
      }), "\n", _jsx(_components.h2, {
        id: "detailed-schema",
        children: "Detailed schema"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes to the ", _jsx(_components.code, {
          children: "sync_changelog"
        }), ", ", _jsx(_components.code, {
          children: "sync_snapshot"
        }), ", and ", _jsx(_components.code, {
          children: "sync_runs"
        }), " tables after each sync. If you enabled ", _jsx(_components.strong, {
          children: "audience snapshots"
        }), ", you'll also find a ", _jsx(_components.code, {
          children: "hightouch_planner.audience_membership"
        }), " table — see the ", _jsx(_components.a, {
          href: "/customer-studio/audience-snapshots",
          children: "audience snapshot docs"
        }), " for its schema."]
      }), "\n", _jsx(_components.h3, {
        id: "changelog-table",
        children: "Changelog table"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "hightouch_audit.sync_changelog"
        }), " table is a log of all changes across all sync runs. A row synced in multiple runs has multiple entries."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "COLUMN"
            }), _jsx(_components.th, {
              children: "DESCRIPTION"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_run_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "op_type"
              })
            }), _jsx(_components.td, {
              children: "Whether the row was added, changed, or removed relative to the last run. Hightouch computes this when planning the run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "The value of the row's primary key, as defined on the model."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "status"
              })
            }), _jsxs(_components.td, {
              children: ["Whether the row synced to the destination: ", _jsx(_components.code, {
                children: "succeeded"
              }), " (the row synced), ", _jsx(_components.code, {
                children: "failed"
              }), " (Hightouch tried but the row failed), or ", _jsx(_components.code, {
                children: "aborted"
              }), " (Hightouch planned to sync the row but didn't try, such as when a run is canceled or hits a fatal error that ends it early)."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "failure_reason"
              })
            }), _jsxs(_components.td, {
              children: ["If the status is ", _jsx(_components.code, {
                children: "failed"
              }), ", a string describing why the row failed."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fields"
              })
            }), _jsxs(_components.td, {
              children: ["A JSON object of the raw model data synced to the destination. This is the raw warehouse data, not the payload Hightouch sent. This column has limitations in Redshift — see ", _jsx(_components.a, {
                href: "#what-are-the-limitations-in-redshift",
                children: "the FAQ"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "split_group"
              })
            }), _jsxs(_components.td, {
              children: ["(Optional) The ", _jsx(_components.a, {
                href: "/customer-studio/experiments",
                children: "experiment group"
              }), " name. Not created if no syncs use experiments."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "snapshot-table",
        children: "Snapshot table"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "hightouch_audit.sync_snapshot"
        }), " table stores each row's current status as of the most recent run, even if the row wasn't synced in that run. After each run, the old statuses are dropped and replaced."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "COLUMN"
            }), _jsx(_components.th, {
              children: "DESCRIPTION"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "op_type"
              })
            }), _jsx(_components.td, {
              children: "Whether the row was added, changed, or unchanged relative to the last run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "The value of the row's primary key, as defined on the model."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "status"
              })
            }), _jsxs(_components.td, {
              children: ["The status of the row. See the ", _jsx(_components.code, {
                children: "sync_changelog.status"
              }), " description for possible values."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "failure_reason"
              })
            }), _jsxs(_components.td, {
              children: ["If the status is ", _jsx(_components.code, {
                children: "failed"
              }), ", a string describing why the row failed."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fields"
              })
            }), _jsxs(_components.td, {
              children: ["The model fields for this row. See the ", _jsx(_components.code, {
                children: "sync_changelog.fields"
              }), " description."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "split_group"
              })
            }), _jsxs(_components.td, {
              children: ["(Optional) The ", _jsx(_components.a, {
                href: "/customer-studio/experiments",
                children: "experiment group"
              }), " name. Not created if no syncs use experiments."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "sync-runs-table",
        children: "Sync runs table"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "hightouch_audit.sync_runs"
        }), " table stores metadata about each run. ", _jsx(_components.code, {
          children: "JOIN"
        }), " it to the ", _jsx(_components.code, {
          children: "sync_changelog"
        }), " table on ", _jsx(_components.code, {
          children: "sync_run_id"
        }), ", and to the ", _jsx(_components.code, {
          children: "sync_snapshot"
        }), " table on ", _jsx(_components.code, {
          children: "sync_id"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "COLUMN"
            }), _jsx(_components.th, {
              children: "DESCRIPTION"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_run_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "primary_key"
              })
            }), _jsx(_components.td, {
              children: "The primary key column of the sync, as defined on the model."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "destination"
              })
            }), _jsx(_components.td, {
              children: "The destination type, such as Salesforce or Braze."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model_name"
              })
            }), _jsx(_components.td, {
              children: "The name of the model attached to the sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the model attached to the sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "status"
              })
            }), _jsxs(_components.td, {
              children: ["The status of the run, either ", _jsx(_components.code, {
                children: "succeeded"
              }), " or ", _jsx(_components.code, {
                children: "failed"
              }), ". The per-row results are usually a better signal."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "error"
              })
            }), _jsx(_components.td, {
              children: "The sync-level error if the run ended early."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "started_at"
              })
            }), _jsx(_components.td, {
              children: "When the run started."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "finished_at"
              })
            }), _jsx(_components.td, {
              children: "When the run finished."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_planned_add"
              })
            }), _jsx(_components.td, {
              children: "The number of planned adds."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_planned_change"
              })
            }), _jsx(_components.td, {
              children: "The number of planned changes."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_planned_remove"
              })
            }), _jsx(_components.td, {
              children: "The number of planned removes."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_attempted_add"
              })
            }), _jsx(_components.td, {
              children: "The number of planned adds that were attempted."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_attempted_change"
              })
            }), _jsx(_components.td, {
              children: "The number of planned changes that were attempted."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_attempted_remove"
              })
            }), _jsx(_components.td, {
              children: "The number of planned removes that were attempted."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_succeeded_add"
              })
            }), _jsx(_components.td, {
              children: "The number of planned adds that synced successfully."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_succeeded_change"
              })
            }), _jsx(_components.td, {
              children: "The number of planned changes that synced successfully."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_succeeded_remove"
              })
            }), _jsx(_components.td, {
              children: "The number of planned removes that synced successfully."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_failed_add"
              })
            }), _jsx(_components.td, {
              children: "The number of attempted adds that failed to sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_failed_change"
              })
            }), _jsx(_components.td, {
              children: "The number of attempted changes that failed to sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "num_failed_remove"
              })
            }), _jsx(_components.td, {
              children: "The number of attempted removes that failed to sync."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "whats-the-performance-impact-of-enabling-warehouse-sync-logs",
        children: "What's the performance impact of enabling Warehouse Sync Logs?"
      }), "\n", _jsx(_components.p, {
        children: "Low. Logs reuse data the Lightning sync engine already produces, and Hightouch writes the rows only after syncing to the destination, so there's no effect on destination throughput. Pruning entries from the log tables is safe and doesn't affect future syncs, though pruned rows aren't rewritten."
      }), "\n", _jsx(_components.h3, {
        id: "what-are-the-limitations-in-redshift",
        children: "What are the limitations in Redshift?"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["Because ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/redshift/latest/dg/r_Character_types.html#r_Character_types-storage-and-ranges",
          children: "Redshift doesn't support strings longer than 65,535 bytes"
        }), ", Hightouch can't store JSON of arbitrary length in the logs. It attempts to store model data in the ", _jsx(_components.code, {
          children: "fields"
        }), " column of ", _jsx(_components.code, {
          children: "sync_changelog"
        }), " and ", _jsx(_components.code, {
          children: "sync_snapshot"
        }), ", but if the model has long strings or many fields, some values may be truncated. Truncated values in those columns don't mean the data was truncated when Hightouch synced to the destination — it's purely a limitation of the warehouse logs."]
      }), "\n"]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
